// Frontend-facing workspace types. The canonical sandbox/production values
// live in @/server/types/org and the persona vocabulary in
// @/server/constants/persona; this file re-exports them and adds the types the
// dashboard's workspace surfaces share.

import { Building2, User, Users, type LucideIcon } from "lucide-react";
import { ORG_ENVIRONMENT, type OrgEnvironment } from "@/server/types/org";
import {
  ACCOUNT_TYPE,
  DESIGNATIONS,
  REQUESTER_TYPE,
  type AccountType,
  type Designation,
  type RequesterType,
} from "@/server/constants/persona";
import type { Organization } from "@/server/schema/org";

export { ORG_ENVIRONMENT, type OrgEnvironment };
export { ACCOUNT_TYPE, DESIGNATIONS, type AccountType, type Designation };
export { REQUESTER_TYPE, type RequesterType };

/** One switcher row. A Sandbox + Production pair is one group; an unpaired workspace is a group of one. */
export type WorkspaceGroup = {
  key: string;
  members: Organization[];
  /** The entry whose name and logo the row renders — production when paired. */
  primary: Organization;
  isPaired: boolean;
};

/** One half of a workspace pair — the id to switch to and the environment it serves. */
export type WorkspaceEnvironmentOption = {
  id: string;
  environment: OrgEnvironment;
};

/** What `useWorkspacePersona` reports about the active workspace. */
export type WorkspacePersona = {
  /**
   * True when the active workspace is Workflow 1 — the owner is the patient.
   * False while unknown, so a persona-only affordance never flashes.
   */
  isIndividual: boolean;
  /** True until the workspace is known, including while the session resolves. */
  isLoading: boolean;
  /** The active workspace's id, for the SMART link call. */
  organizationId: string | null;
  /** The active workspace's name, for the consent copy. */
  organizationName: string | null;
  /**
   * Which environment the active workspace serves. Health system search is
   * scoped to it, so any surface that searches has to be able to say which one
   * the reader is in. Defaults to production while unknown.
   */
  environment: OrgEnvironment;
};

/** The full-page steps of the workspace setup form. */
export const STEP = {
  WORKSPACE: "workspace",
  DETAILS: "details",
  CONSENT: "consent",
} as const;

/**
 * Which full-page step is on screen. Step one names the workspace and asks how
 * it will be used. Step two is `details` for other people's records, collecting
 * who the owner is and where they are, and `consent` for own records, where the
 * owner reads how their records are handled and agrees. A backfill holding
 * several patients starts on `details` and never sees `workspace`, because its
 * patients have already answered the question.
 */
export type Step = (typeof STEP)[keyof typeof STEP];

/** One answer in a choice group. `audience` is the extra muted line, if any. */
export type ChoiceOption<T extends string> = {
  value: T;
  title: string;
  description: string;
  audience?: string;
};

/** How much of the six-column details grid one field takes. */
export type FieldSpan = "third" | "half" | "full";

/** Display label per environment (TeamSwitcher subtitle, workspace list). */
export const ORG_ENVIRONMENT_LABEL: Record<OrgEnvironment, string> = {
  [ORG_ENVIRONMENT.SANDBOX]: "Sandbox",
  [ORG_ENVIRONMENT.PRODUCTION]: "Production",
};

/** Segments rendered by the workspace environment toggle in the switcher. */
export const ENVIRONMENT_TOGGLE_OPTIONS = [
  {
    value: ORG_ENVIRONMENT.SANDBOX,
    label: ORG_ENVIRONMENT_LABEL[ORG_ENVIRONMENT.SANDBOX],
  },
  {
    value: ORG_ENVIRONMENT.PRODUCTION,
    label: ORG_ENVIRONMENT_LABEL[ORG_ENVIRONMENT.PRODUCTION],
  },
] as const;

/**
 * Where a freshly created workspace lands. Never a reload: the URL in the bar
 * belongs to the workspace that was active a moment ago, and its patient ids do
 * not resolve in the new one.
 */
export const WORKSPACE_HOME_PATH = "/dashboard/patients";

/** Heading of the first step, where the workspace is named and categorized. */
export const WORKSPACE_NAME_QUESTION = "Create your workspace";

/** Sub-copy under the first step's heading. */
export const WORKSPACE_NAME_SUBHEADING =
  "Your organization, your family, or whatever you want to call this space.";

/** Label on the name field. */
export const WORKSPACE_NAME_LABEL = "Workspace name";

/**
 * The one question that decides the workflow: whose records get connected.
 *
 * Deliberately not named with the workspace. The name field sits on the same
 * page, so interpolating it rewrote the question on every keystroke.
 */
export const PERSONA_QUESTION = "Who will this workspace fetch records for?";

/** Sub-copy under the persona question, on both the signup and backfill paths. */
export const PERSONA_SUBHEADING = "Whose health records will this workspace hold?";

/**
 * The two answers, in form order. Deliberately phrased as own-records vs.
 * other-people's-records: a family head and a solo advocate both belong on the
 * second option, so nothing here asks whether you are a company. `audience`
 * lists who an answer covers, and only the other-people answer needs it, since
 * "my own records" names its audience exactly.
 *
 * Each body states its ceiling, not just its centre of gravity. The owner of an
 * other-people workspace can be one of its patients, so that answer says so; the
 * own-records answer says "the only patient", because it cannot take a second one
 * and the server refuses to add one. Without both halves a family head who also
 * wants their own records picks the answer that excludes their family.
 */
export const PERSONA_OPTIONS = [
  {
    value: ACCOUNT_TYPE.INDIVIDUAL,
    title: "My own records",
    description:
      "I am the only patient. I connect my own records from the hospitals, clinics and insurers I use.",
  },
  {
    value: ACCOUNT_TYPE.ORGANIZATION,
    title: "Records for other people",
    description:
      "I send each patient a link, they sign in to their own portals, and I can connect my own records here too.",
    audience:
      "A family head managing relatives, an advocate helping members, or a firm collecting records for clients.",
  },
] as const;

/** The sub-question the other-people branch asks before collecting details. */
export const REQUESTER_QUESTION = "Are you an individual or an organization?";

/**
 * The two sub-personas, in form order. Both stay on the "records for other
 * people" branch, so neither answer implies the owner is the patient.
 */
export const REQUESTER_OPTIONS = [
  {
    value: REQUESTER_TYPE.PERSON,
    title: "An individual",
    description: "I do this on my own, for my family or for the people I help.",
  },
  {
    value: REQUESTER_TYPE.ORGANIZATION,
    title: "An organization",
    description: "A company, clinic or firm doing this for the people it serves.",
  },
] as const;

/** Step order, and the label each pill carries. */
export const STEP_LABEL: Record<Step, string> = {
  [STEP.WORKSPACE]: "Your workspace",
  [STEP.DETAILS]: "Your details",
  [STEP.CONSENT]: "Your privacy",
};

/** The mark inside each persona card's tile, looked up by answer. */
export const PERSONA_ICON: Record<AccountType, LucideIcon> = {
  [ACCOUNT_TYPE.INDIVIDUAL]: User,
  [ACCOUNT_TYPE.ORGANIZATION]: Users,
};

/** The mark inside each sub-persona card's tile, looked up by answer. */
export const REQUESTER_ICON: Record<RequesterType, LucideIcon> = {
  [REQUESTER_TYPE.PERSON]: User,
  [REQUESTER_TYPE.ORGANIZATION]: Building2,
};
